import { IconButton, Tooltip, Switch, FormControlLabel, TableRow, Box } from '@mui/material';
import MUIDataTable, { MUIDataTableColumn, FilterType, MUIDataTableProps, MUIDataTableOptions, TableBodyCell } from 'mui-datatables';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import UWCustomToggle from '../../../../../components/molecules/UWCustomToggle';
import {
    Autorenew,
    ListAlt,
    ThumbsUpDown,
    CallMissed, RequestQuote,
    TrendingUp
} from "@mui/icons-material";
import * as format from "../../../../../utils/formatting"
import { useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';

type UnderwritingTableBase = Pick<MUIDataTableProps, "data"> & {
    title: string,
    onRowClick: (modelID: number) => void
    options?: MUIDataTableOptions,
    columns?: MUIDataTableColumn[]
}
export interface IModelsUnderwritingTableProps extends UnderwritingTableBase {
    droppableId?: string,
    onDragEnd?: (result: DropResult) => void
}
const ModelsUnderwritingTable: React.FunctionComponent<IModelsUnderwritingTableProps> = ({ droppableId, onDragEnd, ...props }) => {
    const [view, setView] = useState<ViewType>("basic");
    const [disableDrag, setDisableDrag] = useState(false);

    return (
        <DragDropContext onDragEnd={onDragEnd ? onDragEnd : () => { }}>
            <Droppable droppableId={droppableId ? droppableId : ""} direction="vertical">
                {droppableProvided => (
                    <Box ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                        <MUIDataTable
                            title={props.title}
                            data={props.data}
                            columns={!props.columns ? modelsColumns(view) : props.columns}
                            options={{
                                ...props.options, customToolbar: function () {
                                    return (<UWCustomToggle isActive={view}
                                        handleToggleClick={() => setView(view === "basic" ? "detailed" : "basic")} />)
                                },
                                filterType: "multiselect",
                                customRowRender: onDragEnd && droppableId ? function (data, dataIndex, rowIndex) {
                                    const modelID = data[0];
                                    return (
                                        <Draggable
                                            key={modelID.toString()}
                                            draggableId={modelID.toString()}
                                            index={rowIndex}
                                            isDragDisabled={disableDrag}
                                        >
                                            {(provided, snapshot) => {
                                                let activeTransform = {};
                                                let transform = provided.draggableProps.style?.transform;
                                                if (transform) {
                                                    activeTransform = {
                                                        transform: `translate(0, ${transform.substring(
                                                            transform.indexOf(",") + 1,
                                                            transform.indexOf(")")
                                                        )})`,
                                                    };
                                                }
                                                const onDrag = snapshot.isDragging && {
                                                    backgroundColor: "#68c168",
                                                };
                                                const otherProps = {
                                                    ...provided.draggableProps,
                                                };
                                                return (<TableRow
                                                    className={classNames(snapshot.isDragging && 'draggingRow')}
                                                    {...otherProps}
                                                    {...provided.dragHandleProps}
                                                    ref={provided.innerRef}
                                                    onClick={() => {
                                                        props.onRowClick(modelID);
                                                    }}
                                                    sx={{
                                                        ...provided.draggableProps.style,
                                                        ...activeTransform,
                                                        ...onDrag,
                                                    }}
                                                >
                                                    {data.map(
                                                        (details, index) =>
                                                            modelsColumns(view)[index].options?.display !== "false" && (
                                                                <TableBodyCell
                                                                    key={`m_${index}`}
                                                                    options={{ onCellClick: () => { } }}
                                                                >
                                                                    {details}
                                                                </TableBodyCell>
                                                            )
                                                    )}
                                                </TableRow>
                                                )
                                            }}
                                        </Draggable>
                                    )
                                } : undefined, onRowClick: !onDragEnd && !droppableId ? (data) => {
                                    const modelID = parseInt(data[0]);
                                    props.onRowClick(modelID)
                                } : undefined
                                ,
                                //@ts-ignore
                                onFilterChange: (changedColumn: string, filterList: any[], type: FilterType | "chip" | "reset") => {
                                    if (disableDrag !== true) {
                                        setDisableDrag(true)
                                    }
                                    if (disableDrag && type === "reset") {
                                        setDisableDrag(false);
                                    }
                                    const filters = Object.values(filterList).filter(fl => fl.length > 0).length === 0 ? false : true;
                                    if (disableDrag && !filters) {
                                        setDisableDrag(false);
                                    }
                                },
                                onSearchChange: (searchString) => {
                                    if (!disableDrag && ((searchString?.length ?? 0) > 0)) {
                                        setDisableDrag(true);
                                    }
                                    if (disableDrag && !searchString) {
                                        setDisableDrag(false);
                                    }
                                }
                            }}
                        />
                        {droppableProvided.placeholder}
                    </Box>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export type ViewType = "detailed" | "basic";

const modelsColumns = (view: ViewType): MUIDataTableColumn[] => [
    {
        name: "modelID",
        options: {
            display: "false",
            filter: false,
        },
    },
    {
        name: "name",
        label: "Model Name",
        options: {
            hint: "Model",
            filterType: "textField",
        },
    },
    {
        name: "modelType",
        label: "Type",
        options: {
            hint: "Type",
            customBodyRender: (cell: string) => {
                const type = cell ? cell : "unknown";
                let icon;
                switch (type) {
                    case "Decision":
                        icon = <ThumbsUpDown />;
                        break;
                    case "Deny":
                        icon = <ListAlt />;
                        break;
                    case "Price":
                        icon = <Autorenew />;
                        break;
                    case "Risk":
                        icon = <CallMissed />;
                        break;
                    case "BidWinRate":
                        icon = <RequestQuote />;
                        break;
                    case "Conversion":
                        icon = <TrendingUp />
                        break;
                    default:
                        icon = "";
                        break;
                }
                return (
                    <Tooltip title={type} >
                        <IconButton size="large">{icon} </IconButton>
                    </Tooltip>
                );
            }, //Above switch decides what icon to display based on the model type
        },
    },
    {
        name: "groupWeight",
        label: "Weight",
        options: {
            filter: false,
            hint: "Weight",
            customBodyRender: format.percentageFormat,
        },
    },
    {
        name: "activePeriod",
        label: "Active",
        options: {
            hint: "Active Date",
            display: 'true',
            filterType: "custom",
            filterOptions: {
                logic: (activePeriod, filters) => {
                    if (filters[0] === 'All') {
                        return false
                    }
                    else {
                        return (moment(activePeriod.substring(13)).isBefore(moment()));
                    }
                },
                display: (filterList, onChange, index, column) => {
                    //console.log(filterList[index])
                    return (
                        <FormControlLabel control={<Switch checked={filterList[index][0] !== "All"} onClick={e => {
                            if (filterList[index][0] === "All") { filterList[index][0] = "Active" }
                            else { filterList[index][0] = "All" }
                            onChange(filterList[index], index, column);
                        }} />} label="Show Only Active Models" />
                    );
                }
            },
            customBodyRender: format.dateRangeFormat,
        },
    },
    {
        name: "principal",
        label: "Principal",
        options: {
            hint: "Principal",
            filterType: "textField",
            customBodyRender: format.numberRangeFormat,
        },
    },
    {
        name: "data",
        label: "Data",
        options: {
            hint: "Data",
            customBodyRender: format.arrayFormat,
            filter: false,
        },
    },
    {
        name: "deny",
        label: "Deny",
        options: {
            hint: "Deny",
            display: view !== "basic" ? "true" : "false",
            filter: false,
            customBodyRender: format.symbolFormat,
        },
    },
    {
        name: "approve",
        label: "Approve",
        options: {
            hint: "Approve",
            display: view !== "basic" ? "true" : "false",
            filter: false,
            customBodyRender: format.symbolFormat,
        },
    },
    {
        name: "apr",
        label: "APR",
        options: {
            hint: "APR",
            display: view !== "basic" ? "true" : "false",
            filter: false,
        },
    },
    {
        name: "priceRange",
        label: "Price Range",
        options: {
            hint: "Price Range",
            display: view !== "basic" ? "true" : "false",
            filter: false,
            customBodyRender: format.numberRangeFormat,
        },
    },
    {
        name: "multiplier",
        label: "Multiplier",
        options: {
            hint: "Multiplier",
            display: view !== "basic" ? "true" : "false",
            filter: false,
        },
    },
    {
        name: "errors",
        label: "Errors",
        options: {
            hint: "Errors",
            filter: false,
        },
    },
    {
        name: "unabletoUnderwrite",
        label: "Unable",
        options: {
            hint: "Unable to Underwrite",
            filter: false,
            customBodyRender: format.arrayCountFormat,
        },
    },
];

export default ModelsUnderwritingTable;
